import React from "react";
import { Link } from "react-router-dom";
import { Check, HelpCircle, Activity, Droplets, Wallet, MessageCircleQuestion } from "lucide-react";
import { FAQ_SECTIONS } from "../mock";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const ACCENTS = {
  general: {
    bar: "from-teal-400 to-emerald-400",
    chip: "bg-teal-50 text-teal-700 ring-teal-100",
    icon: HelpCircle,
    open: "data-[state=open]:bg-teal-50 data-[state=open]:border-teal-300",
    dot: "bg-teal-500",
    trig: "group-data-[state=open]:text-teal-800",
  },
  tms: {
    bar: "from-cyan-400 to-teal-500",
    chip: "bg-cyan-50 text-cyan-700 ring-cyan-100",
    icon: Activity,
    open: "data-[state=open]:bg-cyan-50 data-[state=open]:border-cyan-300",
    dot: "bg-cyan-500",
    trig: "group-data-[state=open]:text-cyan-800",
  },
  acceleratedTms: {
    bar: "from-teal-400 to-cyan-500",
    chip: "bg-teal-50 text-teal-700 ring-teal-100",
    icon: Activity,
    open: "data-[state=open]:bg-teal-50 data-[state=open]:border-teal-300",
    dot: "bg-teal-500",
    trig: "group-data-[state=open]:text-teal-800",
  },
  esketamine: {
    bar: "from-sky-400 to-teal-400",
    chip: "bg-sky-50 text-sky-700 ring-sky-100",
    icon: Droplets,
    open: "data-[state=open]:bg-sky-50 data-[state=open]:border-sky-300",
    dot: "bg-sky-500",
    trig: "group-data-[state=open]:text-sky-800",
  },
  feesInsurance: {
    bar: "from-emerald-400 to-teal-500",
    chip: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    icon: Wallet,
    open: "data-[state=open]:bg-emerald-50 data-[state=open]:border-emerald-300",
    dot: "bg-emerald-500",
    trig: "group-data-[state=open]:text-emerald-800",
  },
};

export default function HomeFAQ({ headingAs: Heading = "h2" }) {
  return (
    <section
      className="relative py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-sand-50 via-brand-50 to-brand-50"
      data-testid="home-faq"
    >
      {/* soft decorative teal shapes */}
      <div className="pointer-events-none absolute -top-16 right-[-6rem] h-80 w-80 rounded-full bg-teal-200/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-[-8rem] h-96 w-96 rounded-full bg-emerald-100/50 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10 reveal">
          <Heading className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl text-brand-900 text-balance">
            Frequently Asked{" "}
            <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">
              Questions
            </span>
          </Heading>
          <p className="mt-4 text-brand-900/60 max-w-2xl mx-auto">
            Everything you need to know about our TMS, Accelerated TMS and Esketamine treatments, fees and getting started at AwareHub.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* Left: the shorter categories stacked */}
          <div className="space-y-6 lg:space-y-8">
            {["general", "acceleratedTms", "esketamine"].map((key, idx) => (
              <FaqCard key={key} section={FAQ_SECTIONS[key]} idx={idx} />
            ))}
          </div>
          {/* Right: the long TMS category, with Fees & Insurance beneath it for balance */}
          <div className="space-y-6 lg:space-y-8">
            <FaqCard section={FAQ_SECTIONS.tms} idx={1} />
            <FaqCard section={FAQ_SECTIONS.feesInsurance} idx={2} />
          </div>
        </div>

        <p className="mt-12 text-center text-brand-900/60 reveal">
          Please reach out to us at{" "}
          <Link to="/contact" className="font-semibold text-teal-600 hover:text-teal-800 underline underline-offset-4 transition-colors" data-testid="home-faq-contact-link">
            AwareHub
          </Link>{" "}
          if you cannot find an answer to your question.
        </p>
      </div>
    </section>
  );
}

export function FaqCard({ section, idx }) {
  const a = ACCENTS[section.key] || ACCENTS.general;
  return (
    <div
      className="reveal rounded-[1.75rem] bg-white/80 backdrop-blur-sm border border-brand-100 p-6 sm:p-7 shadow-[0_28px_70px_-50px_rgba(31,56,53,0.6)]"
      style={{ transitionDelay: `${idx * 60}ms` }}
    >
      <div className="mb-5">
        <h3 className="font-display text-xl sm:text-2xl text-brand-900">{section.title}</h3>
      </div>

      <Accordion type="single" collapsible className="space-y-2">
        {section.faqs.map((item, i) => (
          <AccordionItem
            key={i}
            value={`home-${section.key}-${i}`}
            className={`group rounded-2xl border border-brand-100 bg-sand-50/70 px-5 overflow-hidden transition-colors ${a.open}`}
          >
            <AccordionTrigger className={`text-left font-display text-base text-brand-900 hover:no-underline py-3 ${a.trig}`}>
              <span className="flex items-center gap-2.5">
                <span className={`h-1.5 w-1.5 rounded-full ${a.dot} shrink-0`} />
                {item.q}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-5 pl-4">
              {item.a && (
                <div className="space-y-3">
                  {item.a.map((p, j) => (
                    <p key={j} className="text-brand-900/70 leading-relaxed text-[0.95rem]">{p}</p>
                  ))}
                </div>
              )}
              {item.list && (
                <ul className="space-y-2 mt-2">
                  {item.list.map((li, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-brand-900/70 text-[0.95rem]">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-teal-500" strokeWidth={3} />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {item.pending && (
                <p className="text-brand-900/45 italic text-[0.9rem]">Content for this section is being finalised and will be added soon.</p>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
